import { Avatar, Skeleton } from "@mui/material";
import React from "react";

// Teams stamps each row with a time: today's messages show the clock, anything
// older shows the day it landed on.
const listTime = (createdAt) => {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  if (isNaN(date.getTime())) return "";
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
};

// A voice note or a pin has no text to preview, so name it instead of showing
// the raw payload (a location is stored as "lat,lng"). A file or image sent
// without a caption has no text either, which left the row blank.
// A deleted message keeps its text in the database, so the preview has to be
// told: the row went on showing what the thread already renders as removed.
const previewFor = (message, messageType, isDeleted) => {
  if (isDeleted) return "This message was deleted";
  if (messageType === "voice") return "Voice message";
  if (messageType === "location") return "Location";
  const text = (message || "").replace(/<\/?p>/g, "").trim();
  return text || "Attachment";
};

const ChatUsers = ({
  chatUsers,
  openChat,
  selectedUser,
  selectedChatRoomId,
  isLoading,
  unreadCount,
  draftMessages,
  authUserId,
  clientCompany,
  isChatUserBlurred,
}) => {
  return (
    <div className="bg-[#F5F5F5] h-screen lg:h-[70vh] border-y border-l border-gray-200 rounded-l-lg overflow-auto">
      <div className="bg-[#F5F5F5] flex justify-between items-center h-[70px] sticky top-0 z-10 w-full px-4">
        <h5 className="text-lg font-semibold text-gray-900">Chat</h5>
        <i className="fa-light fa-magnifying-glass"></i>
      </div>

      {isLoading ? (
        Array(6)
          .fill()
          .map((_, index) => <ChatUserSkeleton key={index} />)
      ) : chatUsers?.length > 0 ? (
        <div className="pb-4">
          {chatUsers.map((user) => {
            const roomId = user?.chatRoomId ?? user?.conversationId ?? null;
            const isWorkOrderChat = !!user?.isWorkOrderChat;
            // The work order chat has no counterpart to name it after, and the
            // tech appears twice in this list (their own room and this one), so
            // rows are identified by their room rather than by a person.
            // A row whose counterpart is the viewer themselves is their thread with
            // the client, so it is named after the client company — the same thing
            // the chat header shows. This used to be an FC-owner-only case because
            // techs and customers had no list; now they do, and without it a tech
            // sees their own name where "Tech Domain Pty Ltd" belongs.
            const isOwnEntry = !isWorkOrderChat && user?.id === authUserId;
            const isBlurred = isWorkOrderChat ? false : (isChatUserBlurred?.(user) ?? false);
            const isDeletedUser = !!user?.isDeletedUser;
            return (
              <ChatUser
                key={roomId ?? user?.id}
                activeChat={
                  roomId && selectedChatRoomId
                    ? roomId === selectedChatRoomId
                    : !isWorkOrderChat && selectedUser?.id === user.id
                }
                onClick={isDeletedUser ? undefined : () => openChat(user)}
                isBlurred={isBlurred}
                isDeletedUser={isDeletedUser}
                isWorkOrderChat={isWorkOrderChat}
                isDisabled={!!user?.isDisabled}
                // A tech who works through a freelance company is listed under
                // that company's name — the client deals with the company, not
                // the individual. Direct freelancers keep their own name.
                // freelanceCompanyName wins over the client company: a vendor's
                // tech looking at their own room is looking at their vendor, not
                // at TechDomain.
                firstName={
                  user?.freelanceCompanyName ??
                  (isOwnEntry ? (clientCompany?.name ?? user?.firstName) : user?.firstName)
                }
                lastName={isOwnEntry || user?.freelanceCompanyName ? "" : user?.lastName}
                unreadCount={unreadCount[user?.conversationId ?? user?.chatRoomId] || 0}
                recentMessage={user?.recentMessage?.message}
                messageType={user?.recentMessage?.messageType}
                isRecentMessageDeleted={!!user?.recentMessage?.isDeleted}
                recentMessageAt={user?.recentMessage?.createdAt}
                isOwnLastMessage={user?.recentMessage?.senderId === authUserId}
                recentSenderName={user?.recentMessage?.sender?.firstName}
                profilePicture={
                  isOwnEntry && !user?.freelanceCompanyName
                    ? clientCompany?.logo || user?.profilePicture
                    : user?.profilePicture
                }
                draftMessage={draftMessages?.[user?.id]?.message}
              />
            );
          })}
        </div>
      ) : (
        <p className="px-4 text-sm text-gray-500">No messages yet.</p>
      )}
    </div>
  );
};

export default ChatUsers;

const ChatUser = ({
  onClick,
  activeChat,
  firstName,
  lastName,
  unreadCount,
  recentMessage,
  messageType,
  isRecentMessageDeleted,
  recentMessageAt,
  isOwnLastMessage,
  recentSenderName,
  profilePicture,
  draftMessage,
  isBlurred,
  isDeletedUser,
  isWorkOrderChat,
  isDisabled,
}) => {
  const hasDraft = draftMessage && draftMessage !== "<p><br></p>";
  const isUnread = unreadCount > 0;

  return (
    <div
      onClick={onClick}
      // The transparent border keeps every row the same size, so the card on
      // hover and on selection appears without nudging the layout.
      className={`relative mx-2 flex items-center gap-3 rounded-md border border-transparent px-3 py-2.5 transition ${
        isDeletedUser ? "cursor-not-allowed opacity-50 select-none" : "cursor-pointer"
      } ${
        activeChat
          ? "bg-white border-gray-200 shadow-sm"
          : "hover:bg-gray-50 hover:border-gray-200 hover:shadow-sm"
      } ${isBlurred ? "blur-sm" : ""}`}
    >
      {isWorkOrderChat ? (
        <Avatar sx={{ width: 40, height: 40 }}>
          <i className="fa-light fa-clipboard-list text-base !text-white" />
        </Avatar>
      ) : (
        <Avatar src={isDeletedUser ? undefined : profilePicture} sx={{ width: 40, height: 40 }} />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p
            className={`min-w-0 truncate capitalize text-[13px] ${
              isUnread ? "font-semibold text-gray-900" : "font-medium text-gray-800"
            }`}
          >
            {`${firstName} ${lastName}`}
          </p>
          {isDeletedUser && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-normal normal-case text-gray-500">
              deleted user
            </span>
          )}
          {isDisabled && (
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-normal normal-case text-gray-500">
              closed
            </span>
          )}
          <span className="ml-auto shrink-0 text-[11px] font-light text-gray-500">
            {listTime(recentMessageAt)}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-xs ${
              isUnread ? "text-gray-800" : "text-gray-500"
            }`}
          >
            {hasDraft ? (
              <>
                <span className="text-primary font-semibold">Draft: </span>
                <span
                  dangerouslySetInnerHTML={{
                    __html: (draftMessage || "").replace(/<\/?p>/g, ""),
                  }}
                />
              </>
            ) : (
              <>
                {recentMessageAt &&
                  messageType !== "system" &&
                  (isOwnLastMessage || recentSenderName) && (
                    <span>{isOwnLastMessage ? "You" : recentSenderName}: </span>
                  )}
                {recentMessageAt && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: previewFor(recentMessage, messageType, isRecentMessageDeleted),
                    }}
                  />
                )}
              </>
            )}
          </span>
          {isUnread &&
            (unreadCount > 1 ? (
              <span className="shrink-0 rounded-full bg-primary px-1.5 text-[10px] leading-4 text-white">
                {unreadCount}
              </span>
            ) : (
              <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
            ))}
        </div>
      </div>
    </div>
  );
};

const ChatUserSkeleton = () => {
  return (
    <div className="mx-2 flex items-center gap-3 rounded-md px-3 py-2.5">
      <Skeleton variant="circular">
        <Avatar sx={{ width: 40, height: 40 }} />
      </Skeleton>

      <div className="min-w-0 flex-1">
        <Skeleton variant="text" width={120} height={18} />
        <Skeleton variant="text" width={180} height={14} />
      </div>
    </div>
  );
};
